import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { erroToastify, sucessoToastify } from "../../shared/validacaoToastify";
import {
  getUsuario,
  getUsuarios,
  postFiltroUsuario,
  putBloquearUsuario
} from "../../services/apiUsuario";
import { Helmet } from "react-helmet";
import Filtro from "../../components/filtro";
import TabelaConsulta from "../../components/tabelaConsulta/tabelaConsulta";
import Paginacao from "../../shared/paginacao";
import { ToastContainer } from "react-toastify";
import SelectGenerico from "../../components/selectGenerico/selectGenerico";

interface Usuario {
  cdUsuario: number;
  nmUsuario: string;
  nmEmail: string;
  dsCpf: string;
  dsLogin: string;
  dmBloqueado: boolean;
}

export default function ConsultaUsuario() {
  const location = useLocation();
  const navigate = useNavigate();
  const [ordem, setOrdem] = useState<string>("asc");
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [radioValue, setRadioValue] = useState<number>(10);
  const [paginaAtual, setPaginaAtual] = useState<number>(1);
  const [totalPaginas, setTotalPaginas] = useState<number>(0);
  const [filtroAtivo, setFiltroAtivo] = useState<boolean>(false);
  const [totalRegistros, setTotalRegistros] = useState<number>(0);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState<Usuario[]>([]);
  const [bloqueou, setBloqueou] = useState<boolean>(false);
  const [filtroAplicado, setFiltroAplicado] = useState<
    | {
      [key: string]: string;
    }
    | undefined
  >(undefined);
  const previousUsuariosRef = useRef<Usuario[]>([]);

  const camposFiltro = [
    { label: "Nome", campo: "nmUsuario" },
    { label: "E-mail", campo: "nmEmail" },
    { label: "Login", campo: "dsLogin" },
    { label: "CPF", campo: "dsCPF" },
  ];
  const options = [
    { valor: 10, rotulo: "10" },
    { valor: 30, rotulo: "30" },
    { valor: 50, rotulo: "50" }
  ];
  const aplicarFiltroWrapper = (filtro: { [key: string]: string }) => {
    aplicarFiltro(filtro, true);
  };

  const trocarPagina = (pageNumber: number) => {
    setPaginaAtual(pageNumber);
  };

  const alternarOrdem = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    setOrdem(selectedValue);
    setPaginaAtual(1);
  };

  const alterarValorRadio = (value: number) => {
    setRadioValue(value);
    setPaginaAtual(1);
  };

  const contarTotalUsuario = async () => {
    try {
      const response = await getUsuarios("1", "1", "asc");
      return response.contadorUsuarios;
    } catch (error) {
      console.error("Erro ao chamar o contador:", error);
    }
  };

  const aplicarFiltro = async (
    filtro: { [key: string]: string },
    novoFiltro: boolean
  ) => {
    try {
      const response = await postFiltroUsuario(
        filtro,
        novoFiltro ? "1" : paginaAtual.toString(),
        radioValue.toString(),
        ordem
      );
      const usuariosFiltro = response.listaUsuariosEncontrados;

      const usuariosComBloqueioCorrigido = await Promise.all(
        usuariosFiltro.map(async (usuario: Usuario) => {
          const userData = await getUsuario(usuario.cdUsuario);

          return {
            ...usuario,
            dmBloqueado: userData.data.dmBloqueado || false
          };
        })
      );
      const contadorUsuarios = response.contadorUsuarios;

      setUsuariosFiltrados(usuariosComBloqueioCorrigido);
      setTotalRegistros(contadorUsuarios);
      setTotalPaginas(Math.ceil(contadorUsuarios / radioValue));
      setPaginaAtual(novoFiltro ? 1 : paginaAtual);
      setFiltroAtivo(true);
      setFiltroAplicado(filtro);

    } catch (error: any) {
      if (error.response.data) erroToastify(error.response.data);
    }
  };


  const limparFiltro = async () => {
    try {
      setUsuariosFiltrados([]);
      setFiltroAtivo(false);
      const totalUsuarios = await contarTotalUsuario();
      setTotalRegistros(totalUsuarios);
      setPaginaAtual(1);
      setFiltroAplicado(undefined);
    } catch (error) {
      console.error("Erro ao limpar filtro:", error);
    }
  };

  const bloquearDesbloquearUsuario = async (userId: number, isBlocked: boolean) => {
    try {
      await putBloquearUsuario({
        cdUsuario: userId,
        dmBloqueado: isBlocked
      });

      const updatedUsers = usuarios.map(user =>
        user.cdUsuario === userId ? { ...user, dmBloqueado: !isBlocked } : user
      );
      setUsuarios(updatedUsers);

      setBloqueou(true);

      sucessoToastify("Operação concluída com sucesso.");

      setTimeout(() => {
        setBloqueou(false);
      }, 1000);

      if (filtroAplicado) {
        await aplicarFiltro(filtroAplicado, true); 
      } else {
        buscarDados(paginaAtual, radioValue, true, ordem); 
      }
    } catch (error) {
      erroToastify("Erro ao bloquear/desbloquear usuário.");
    }
  };

  const buscarDados = async (
    pagina: number,
    registrosPorPagina: number,
    novoFiltro: boolean,
    ordem: string
  ) => {
    try {
      if (filtroAplicado) {
        await aplicarFiltro(filtroAplicado, novoFiltro);
      } else {
        const responseUsuarios = await getUsuarios(
          pagina.toString(),
          registrosPorPagina.toString(),
          ordem
        );
        const usuariosData = responseUsuarios.listaUsuarios || [];

        const usuariosComBloqueioCorrigido = await Promise.all(
          usuariosData.map(async (usuario: Usuario) => {
            const userData = await getUsuario(usuario.cdUsuario);
            return {
              ...usuario,
              dmBloqueado: userData.data.dmBloqueado || false
            };
          })
        );

        const contadorUsuarios = responseUsuarios.contadorUsuarios || 0;
        setTotalRegistros(contadorUsuarios);
        setTotalPaginas(Math.ceil(contadorUsuarios / registrosPorPagina));
        setUsuarios(usuariosComBloqueioCorrigido);
        setPaginaAtual(pagina);
      }
    } catch (error) {
      console.error("Erro ao buscar usuário:", error);
    }
  };

  useEffect(() => {
    buscarDados(paginaAtual, radioValue, false, ordem);
  }, [paginaAtual, radioValue, ordem,filtroAplicado]);

  useEffect(() => {
    buscarDados(paginaAtual, radioValue, false, ordem);

  }, [bloqueou])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const successMessage = searchParams.get("successMessage");
    if (successMessage) {
      sucessoToastify(successMessage);
      navigate("/milshop/usuario/consultar");
    }
  }, [location.search, navigate]);

  return (
    <>
      <Helmet>
        <title>Consulta usuário</title>
      </Helmet>
      <div className="d-flex flex-column justify-content-center align-items-center">
        <Filtro
          campo={camposFiltro}
          onFiltrar={aplicarFiltroWrapper}
          limparFiltro={limparFiltro}
        />
        <div className="d-flex justify-content-between w-75">
          <div className="d-flex align-items-center">
            <p className="ms-25 m-0 text-center me-2">Ordenar:</p>
            <div className="col-auto">
              <select
                className="form-select filtro-select"
                aria-label="Small select example"
                onChange={alternarOrdem}
              >
                <option value="asc">A-Z</option>
                <option value="desc">Z-A</option>
              </select>
            </div>
          </div>
          <div className="d-flex align-items-center me-2 mb-2">
            <p className="ms-25 m-0 text-center me-2">Registros por página:</p>
            <SelectGenerico
              opcoes={options}
              valorSelecionado={radioValue}
              onChange={(value) => alterarValorRadio(Number(value))}
              opcaoPadrao="Selecione"
              classe="form-select filtro-select selectRegistroPagina"
              temLabel={false}
            />
          </div>
        </div>
        <div className="w-75">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="ms-25 m-0 text-center">
              {totalRegistros} resultados, Página {paginaAtual}
            </p>
            <button
              type="button"
              role="button"
              style={{ width: "100px" }}
              className="btn btn-outline-primary me-2 btn-sm custom-btn"
              onClick={() => navigate("/milshop/usuario/incluir")}
            >
              Incluir
            </button>
          </div>
          <div className="table-responsive">
            <TabelaConsulta
              exibirIconeBloqueio={true}
              dados={
                usuariosFiltrados && usuariosFiltrados.length > 0
                  ? usuariosFiltrados
                  : usuarios.map(usuario => ({
                    ...usuario,
                    dmBloqueado: usuario.dmBloqueado || false
                  }))
              }
              colunas={[
                { chave: "nmUsuario", titulo: "Nome" },
                { chave: "nmEmail", titulo: "E-mail" },
                { chave: "dsLogin", titulo: "Apelido" },
                { chave: "dsCpf", titulo: "CPF" },
                {
                  chave: "dmBloqueado",
                  titulo: "Bloqueio",
                  render: (isBlocked: boolean, usuario: Usuario) => (
                    <button
                      onClick={() => bloquearDesbloquearUsuario(usuario.cdUsuario, isBlocked)}
                      className="btn btn-sm"
                      style={{
                        backgroundColor: isBlocked ? "red" : "green",
                        color: "white"
                      }}
                    >
                      {isBlocked ? "Desbloquear" : "Bloquear"}
                    </button>
                  )
                }
              ]}
              cdEntidade={"cdUsuario"}
              entidade="usuario"
              onToggleLock={bloquearDesbloquearUsuario}
            />
          </div>
          <Paginacao
            paginaAtual={paginaAtual}
            totalPaginas={totalPaginas}
            onPageChange={trocarPagina}
          />
        </div>
      </div>
      <ToastContainer />
    </>
  );
}
